import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, ListChecks, Loader2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const EmailListModal = ({ open, onClose, list, onSave }) => {
  const initialState = { name: "", description: "" };
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (list) {
      setForm({ name: list.name || "", description: list.description || "" });
    } else {
      setForm(initialState);
    }
  }, [list, open]);

  const handleClose = () => {
    setForm(initialState);
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warn("Ingresa un nombre para la lista");
      return;
    }
    setSaving(true);
    try {
      if (list) {
        await api.put(`/email-marketing/lists/${list.id}`, form);
        toast.success("Lista actualizada");
      } else {
        await api.post("/email-marketing/lists", form);
        toast.success("Lista creada");
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-indigo-600" />
            {list ? "Editar Lista" : "Nueva Lista"}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Nombre
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              autoFocus
              placeholder="Ej: Clientes VIP, Newsletter"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Descripción (opcional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {list ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailListModal;
